import { apiClient } from './client'
import type { CursorPage } from './accounts'
import type {
  AssignmentView,
  InvitationView,
  InviteUserRequest,
  ProfileSummary,
  RoleView,
  TerritoryView,
  UserSearchResult,
  UserView,
} from '../types/userAdmin'

/**
 * User-admin-entra-federation Slice B (design §2.2) — `/api/v1/users` + assignment sub-resources +
 * the read-only Profile/Role/Territory catalogs. Every call here is capability-gated server-side
 * (`user:F`/`profile_assignment:F`/`permission_set:F`/`role:F`/`territory:F`) — a denied caller
 * gets a real 403 `ApiError`, not a silently-empty response.
 */

export interface ListUsersParams {
  cursor?: string | null
  limit?: number
}

function toQuery(params: ListUsersParams): string {
  const q = new URLSearchParams()
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

export async function listUsers(params: ListUsersParams, signal?: AbortSignal): Promise<CursorPage<UserView>> {
  const { data } = await apiClient.get<CursorPage<UserView>>(`/users?${toQuery(params)}`, { signal })
  return data
}

export async function getUser(id: string, signal?: AbortSignal): Promise<UserView> {
  const { data } = await apiClient.get<UserView>(`/users/${id}`, { signal })
  return data
}

/**
 * IB-328 — the reverse-lookup/typeahead directory search (`GET /api/v1/users/search`). Deliberately
 * NOT gated behind `user:F` server-side (see `UserController.search` javadoc) — any authenticated
 * in-tenant principal reaching a picker can call this; the picked id's ASSIGNMENT stays
 * capability-gated at its own call site (e.g. Case Team `case:W`).
 */
export async function searchUsers(
  query: string,
  limit = 10,
  signal?: AbortSignal,
): Promise<UserSearchResult[]> {
  const trimmed = query.trim()
  if (!trimmed) return []
  const q = new URLSearchParams()
  q.set('q', trimmed)
  q.set('limit', String(limit))
  const { data } = await apiClient.get<UserSearchResult[]>(`/users/search?${q.toString()}`, { signal })
  return data
}

export async function inviteUser(request: InviteUserRequest): Promise<InvitationView> {
  const { data } = await apiClient.post<InvitationView>('/users', request)
  return data
}

/** `invitationId` — resend targets the PENDING invitation, not an `app_user` id (see `UserController` javadoc). */
export async function resendInvite(invitationId: string): Promise<InvitationView> {
  const { data } = await apiClient.post<InvitationView>(`/users/${invitationId}/resend-invite`, undefined)
  return data
}

export async function deactivateUser(id: string): Promise<void> {
  await apiClient.post<void>(`/users/${id}/deactivate`, undefined)
}

export async function reactivateUser(id: string): Promise<void> {
  await apiClient.post<void>(`/users/${id}/reactivate`, undefined)
}

export async function assignProfile(userId: string, profileKey: string): Promise<void> {
  await apiClient.post<void>(`/users/${userId}/profile`, { profileKey })
}

export async function grantPermissionSet(
  userId: string,
  permissionSetKey: string,
  expiresAt: string | null,
): Promise<AssignmentView> {
  const { data } = await apiClient.post<AssignmentView>(`/users/${userId}/permission-sets`, {
    permissionSetKey,
    expiresAt,
  })
  return data
}

export async function revokePermissionSet(userId: string, assignmentId: string): Promise<void> {
  await apiClient.del(`/users/${userId}/permission-sets/${assignmentId}`)
}

export async function assignRole(userId: string, roleId: string): Promise<void> {
  await apiClient.post<void>(`/users/${userId}/role`, { roleId })
}

export async function assignTerritory(userId: string, territoryId: string): Promise<void> {
  await apiClient.post<void>(`/users/${userId}/territory`, { territoryId })
}

export async function listProfiles(signal?: AbortSignal): Promise<ProfileSummary[]> {
  const { data } = await apiClient.get<ProfileSummary[]>('/profiles', { signal })
  return data
}

export async function listRoles(signal?: AbortSignal): Promise<RoleView[]> {
  const { data } = await apiClient.get<RoleView[]>('/roles', { signal })
  return data
}

export async function listTerritories(signal?: AbortSignal): Promise<TerritoryView[]> {
  const { data } = await apiClient.get<TerritoryView[]>('/territories', { signal })
  return data
}
